import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";

import type { AdminTicketCommentDto, AdminTicketDetailsDto, TicketCommentVisibility, UserDto } from "@/api/types";
import { AnnotationInfoIcon } from "@/components/Icons/AnnotationInfoIcon";
import { Lock01Icon } from "@/components/Icons/Lock01Icon";
import { Button } from "@/design-system/Button";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import {
  CommentComposerActions,
  CommentComposerSurface,
  CommentComposerTypeSelector,
} from "@/product-patterns/tickets/CommentComposer";
import { useConfig } from "@/providers/config/useConfig";
import { useContentFieldManager } from "@/providers/ContentFieldProvider";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { useTranslation } from "@/translations";

import type { TicketCommentPayload, TicketCommentType } from "./helpers";
import { TicketAIQuickDenialModal } from "./TicketAIQuickDenialModal";
import { TicketCommentField } from "./TicketCommentField";
import { TicketCommentFieldAIActions } from "./TicketCommentFieldAIActions";

const MIN_TICKET_RESPONSIBILITY_SUGGESTION_CONFIDENCE = 40;

type TicketCommentFieldExtendedProps = {
  ticket: AdminTicketDetailsDto;
  allowedAIActions?: AIAction[];
  allowedCommentTypes: TicketCommentType[];
  user: UserDto;
  editingComment?: AdminTicketCommentDto;
  isAttachmentAllowed?: boolean;
  onComment: (payload: TicketCommentPayload) => Promise<unknown>;
  onEditComment?: (payload: TicketCommentPayload & { commentId: string }) => Promise<void>;
  onCancelEditComment?: () => void;
};

type AIAction = "responsibilitySuggestion" | "replyGeneration" | "replySuggestion";

enum ResponsibilitySuggestionState {
  UNKNOWN,
  DISAGREED,
}

// A wrapper component to handle additional functionality for ticket comment field
// e.g. AI responsibility suggestion, AI reply suggestion, AI reply generation
export function TicketCommentFieldExtended({
  ticket,
  allowedCommentTypes,
  allowedAIActions,
  user,
  editingComment,
  isAttachmentAllowed = true,
  onComment,
  onEditComment,
  onCancelEditComment,
}: TicketCommentFieldExtendedProps): React.ReactNode {
  const [preferredCommentType, setSelectedCommentType] = useState<TicketCommentType>(allowedCommentTypes[0]);
  const selectedCommentType =
    editingComment || allowedCommentTypes.includes(preferredCommentType)
      ? preferredCommentType
      : allowedCommentTypes.at(0);
  const { t } = useTranslation();

  // Quick denial
  const [isQuickDenialModalOpened, quickDenialModalHandlers] = useBool(false);
  const [responsibilitySuggestionState, setResponsibilitySuggestionState] = useState<ResponsibilitySuggestionState>(
    ResponsibilitySuggestionState.UNKNOWN,
  );

  const sessionUser = useSessionUser();
  const postHog = usePostHog();
  const isAiTicketResponsibilitySuggestionAvailable = useConfig("isAiTicketResponsibilitySuggestionAvailable");
  const { ref, setCurrValue, currValue } = useContentFieldManager();
  const isInlineNobiSuggestionAvailable = postHog.getFeatureFlag("nobi-admin-ticket-reply-suggestion") !== "card";

  const isNobiSuggestionShown =
    !!allowedAIActions?.includes("replySuggestion") &&
    isInlineNobiSuggestionAvailable &&
    sessionUser.isAdmin &&
    selectedCommentType === "public";
  const nobiSuggestedValue = isNobiSuggestionShown && ticket.suggestedResponse ? ticket.suggestedResponse : "";
  const defaultPlaceholder =
    selectedCommentType === "internal"
      ? t("page.tickets.details.comment-field.placeholder-note")
      : t("page.tickets.details.comment-field.placeholder-message");
  const placeholder = nobiSuggestedValue
    ? t("common.placeholder.continue-with-nobi-suggestion", { key: "↓", suggestion: nobiSuggestedValue })
    : defaultPlaceholder;

  const queryClient = useQueryClient();
  const ticketQueries = useTicketQueries();

  const { data: responsibilitySuggestion } = useQuery({
    ...ticketQueries.responsibilitySuggestion(ticket.id),
    enabled: Boolean(ticket?.responsibilityAssignee) && isAiTicketResponsibilitySuggestionAvailable,
  });

  useEffect(() => {
    if (responsibilitySuggestion) {
      void queryClient.prefetchQuery(
        ticketQueries.denialReplySuggestion(ticket.id, {
          additionalInfo: responsibilitySuggestion.description,
        }),
      );
    }
  }, [queryClient, ticketQueries, responsibilitySuggestion, ticket.id]);

  useEffect(() => {
    if (editingComment) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setSelectedCommentType(editingComment.internal ? "internal" : "public");
    }
  }, [editingComment]);

  useEffect(() => {
    ref.current?.classList.toggle("nobi-placeholder", Boolean(nobiSuggestedValue));
  }, [nobiSuggestedValue, ref]);

  useEffect(() => {
    const inputEl = ref.current;
    if (!nobiSuggestedValue || !inputEl) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" && inputEl.value === "") {
        e.preventDefault();
        setCurrValue(nobiSuggestedValue);
      }
    };

    inputEl.addEventListener("keydown", handleKeyDown);

    return () => {
      inputEl.removeEventListener("keydown", handleKeyDown);
    };
  }, [nobiSuggestedValue, ref, setCurrValue]);

  useEffect(() => {
    if (!currValue) {
      return;
    }

    if (currValue === ticket.suggestedResponse) {
      postHog.capture("applied_nobi_admin_ticket_reply_suggestion", {
        source: "keyboard",
      });
    }
  }, [currValue, postHog, ticket.suggestedResponse]);

  function handleSelectCommentType(type: TicketCommentVisibility) {
    setSelectedCommentType(type);
  }

  async function handleSendComment({ content, type, images, videos, documents }: TicketCommentPayload) {
    if (editingComment && onEditComment) {
      await onEditComment({
        commentId: editingComment.id,
        content,
        images,
        videos,
        documents,
        type,
      });
    } else {
      await onComment({
        content,
        images,
        videos,
        documents,
        type,
      });
    }
  }

  function handleExpandSuggestion() {
    quickDenialModalHandlers.setTrue();

    postHog.capture("clicked_expand_ai_responsibility_suggestion", {
      ticketId: ticket.id,
    });
  }

  function handleDisagreeSuggestion() {
    quickDenialModalHandlers.setFalse();
    setResponsibilitySuggestionState(ResponsibilitySuggestionState.DISAGREED);
  }

  // Use has just disgreed with the suggestion
  const hasUserDisagreedWithSuggestion =
    responsibilitySuggestionState === ResponsibilitySuggestionState.DISAGREED &&
    !!responsibilitySuggestion &&
    responsibilitySuggestion.rating === "bad";
  // The suggestion is relevant if it's for the tenant, not rated and has a high enough confidence
  const isResponsibilitySuggestionRelevant =
    !!responsibilitySuggestion &&
    responsibilitySuggestion.assignee === "tenant" &&
    responsibilitySuggestion.rating === "notRated" &&
    responsibilitySuggestion.confidence >= MIN_TICKET_RESPONSIBILITY_SUGGESTION_CONFIDENCE;
  // The suggestion is useful if the ticket is of status `new` and has no comments
  const isResponsibilitySuggestionUseful = ticket?.commentCount === 0 && ticket?.status.type === "new";
  // The suggestion is useful if the ticket is of status `new` and has no comments
  const isResponsibilitySuggestionShown =
    !!allowedAIActions?.includes("responsibilitySuggestion") &&
    isResponsibilitySuggestionUseful &&
    (isResponsibilitySuggestionRelevant || hasUserDisagreedWithSuggestion) &&
    isAiTicketResponsibilitySuggestionAvailable;

  if (!selectedCommentType) {
    return null;
  }

  return (
    <>
      <Stack gap="md">
        <AnimatePresence>
          {isResponsibilitySuggestionShown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.6 }}
            >
              <Notice
                data-testid="ai-responsibility-suggestion-notice"
                variant={hasUserDisagreedWithSuggestion ? "success" : "special"}
                message={
                  hasUserDisagreedWithSuggestion
                    ? t("page.tickets.details.responsibility-suggestion-feedback")
                    : t("page.tickets.details.responsibility-suggestion-tenant")
                }
                icon={<AnnotationInfoIcon />}
                action={
                  hasUserDisagreedWithSuggestion
                    ? undefined
                    : {
                        label: t("common.action.see-more"),
                        onClick: handleExpandSuggestion,
                      }
                }
              />
            </motion.div>
          )}
        </AnimatePresence>
        <Stack>
          <TicketCommentFieldTypeSelector
            selectedType={selectedCommentType}
            allowedCommentTypes={allowedCommentTypes}
            onSelectType={handleSelectCommentType}
            isDisabled={!!editingComment}
          />

          <CommentComposerSurface presentation={selectedCommentType}>
            <TicketCommentField
              user={user}
              onCancelEdit={onCancelEditComment}
              commentInEditing={editingComment}
              onSend={handleSendComment}
              commentType={selectedCommentType}
              placeholder={placeholder}
              actionComponent={
                allowedAIActions &&
                allowedAIActions.length > 0 && (
                  <CommentComposerActions>
                    {allowedAIActions.includes("replyGeneration") && (
                      <TicketCommentFieldAIActions ticketId={ticket.id} />
                    )}
                    {allowedAIActions.includes("responsibilitySuggestion") &&
                      isResponsibilitySuggestionShown &&
                      !hasUserDisagreedWithSuggestion && (
                        <Button variant="ai" onClick={quickDenialModalHandlers.setTrue}>
                          {t("page.tickets.details.quick-denial.button")}
                        </Button>
                      )}
                  </CommentComposerActions>
                )
              }
              isAttachmentAllowed={isAttachmentAllowed}
            />
          </CommentComposerSurface>
        </Stack>
      </Stack>
      {ticket && responsibilitySuggestion && (
        <TicketAIQuickDenialModal
          isOpened={isQuickDenialModalOpened}
          onOpenChange={quickDenialModalHandlers.set}
          description={responsibilitySuggestion.description}
          onDisagree={handleDisagreeSuggestion}
          ticket={ticket}
        />
      )}
    </>
  );
}

interface TicketCommentFieldTypeSelectorProps {
  selectedType: TicketCommentType;
  allowedCommentTypes: TicketCommentType[];
  isDisabled?: boolean;
  onSelectType: (type: TicketCommentType) => void;
}
function TicketCommentFieldTypeSelector({
  selectedType,
  allowedCommentTypes,
  isDisabled,
  onSelectType,
}: TicketCommentFieldTypeSelectorProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <CommentComposerTypeSelector
      allowedTypes={allowedCommentTypes}
      selectedType={selectedType}
      disabled={isDisabled}
      onSelect={onSelectType}
      publicLabel={t("page.tickets.details.comment-field.comment")}
      internalLabel={t("page.tickets.details.comment-field.note")}
      internalIcon={<Lock01Icon />}
    />
  );
}
